import {
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  TableContainer,
  Theme,
  Typography,
} from '@mui/material';
import {DataGrid, DataGridProps, GridColDef, GridColumnResizeParams} from '@mui/x-data-grid';
import {ruRU} from '@mui/x-data-grid/locales';
import {ChangeEvent, CSSProperties, FC, Fragment, useEffect, useRef} from 'react';
import {useTableColumnWidthStore} from '../../store/table';
import {applyColumnWidths, calcColumnWidths} from './helpers';
import {isEmpty} from 'lodash';

const CELL_STYLE = {
  fontSize: '12px',
  outline: 'none',
  userSelect: 'none',
} as const satisfies CSSProperties;

const PAGE_SIZE_DATA = [5, 10, 20, 50, 100] as const;

type OmittedDataGridProps = Omit<
  DataGridProps,
  | 'onColumnWidthChange'
  | 'disableRowSelectionOnClick'
  | 'disableColumnFilter'
  | 'hideFooter'
  | 'slotProps'
  | 'localeText'
>;

export interface TableProps extends OmittedDataGridProps {
  name: string;
  pageSizeTitle?: string;
  pageSize: number;
  totalPages: number;
  currentPage: number;
  totalCount?: number;
  paddingProp?: number;
  startPageFromZero?: boolean;
  tableContainerSx?: SxProps<Theme>;
  onPageSizeChange?: (event: SelectChangeEvent<number>, child: React.ReactNode) => void;
  onPaginationChange?: (event: ChangeEvent<unknown>, page: number) => void;
  columnsManagementGetTogglableColumns?: (columns: GridColDef[]) => GridColDef['field'][];
}

export const Table: FC<TableProps> = ({
  name,
  pageSizeTitle,
  pageSize,
  totalPages,
  currentPage,
  startPageFromZero,
  totalCount,
  paddingProp,
  columns,
  tableContainerSx,
  onPageSizeChange,
  onPaginationChange,
  columnsManagementGetTogglableColumns,
  ...props
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const {data, changeColumnData, setDefaultData} = useTableColumnWidthStore(name);
  const localPage = startPageFromZero ? currentPage + 1 : currentPage;

  const slotProps: DataGridProps['slotProps'] = columnsManagementGetTogglableColumns
    ? {
        cell: {style: CELL_STYLE},
        columnsManagement: {getTogglableColumns: columnsManagementGetTogglableColumns},
      }
    : {cell: {style: CELL_STYLE}};

  const handlePageChange = (e: ChangeEvent<unknown>, page: number) => {
    const newPage = startPageFromZero ? page - 1 : page;
    onPaginationChange?.(e, newPage);
  };

  const handleColumnWidthChange = ({colDef, width}: GridColumnResizeParams) => {
    changeColumnData(colDef.field, width);
  };

  useEffect(() => {
    if (!isEmpty(data)) return;
    const tableWidth = ref.current?.offsetWidth ?? 0;
    const columnWidths = calcColumnWidths(columns, tableWidth, !!props?.checkboxSelection, paddingProp);
    setDefaultData(columnWidths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableContainer sx={tableContainerSx ?? {px: 2}}>
      <DataGrid
        ref={ref}
        localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
        slotProps={slotProps}
        hideFooter
        disableColumnFilter
        disableRowSelectionOnClick
        onColumnWidthChange={handleColumnWidthChange}
        columns={applyColumnWidths(columns, data)}
        sortingMode="server"
        {...props}
      />
      <Stack direction="row" spacing={4} sx={{m: 2}} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          {totalCount ? <Fragment>Количество: {totalCount}</Fragment> : null}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography sx={{m: 0}}>{pageSizeTitle || 'Строк на странице:'}</Typography>
            <Select variant="standard" size="small" value={pageSize} onChange={onPageSizeChange}>
              {PAGE_SIZE_DATA.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Pagination count={totalPages} size="medium" page={localPage} onChange={handlePageChange} />
        </Stack>
      </Stack>
    </TableContainer>
  );
};
